import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CustomBreadcrumb from "../CommonComponent/CommonBreadcrumb";
import CommonTabs from "../CommonComponent/CommonTabs";
import CommonHeader from "../CommonComponent/CommonHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  assignUser,
  generatePresignedUrl,
  getDataRequest,
  getPresignedUrl,
  updateStatusChange,
} from "../api/dataRequest";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import { getUserData } from "../api/user";
import Loader from "../CommonComponent/Loader";
import { getWebsiteById } from "../api/websiteService";
import { getFormattedTimestamp } from "../utils/dateUtils";
import { fetchUserAttributes } from "@aws-amplify/auth";
import { CloseOutlined, FileDownload, UploadFile } from "@mui/icons-material";
import ButtonCommon from "../CommonComponent/ButtonCommon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const RequestLog = ({
  status,
  createdOn,
  closedOn,
  closedBy,
  comment,
  attachment,
}) => {
  const handleDownload = async (fileName) => {
    try {
      const response = await getPresignedUrl(fileName);
      if (response.status_code === 200 && response.success) {
        const presignedUrl = response.data.url;
        const fileResponse = await fetch(presignedUrl);
        if (!fileResponse.ok) {
          throw new Error("Failed to download the file");
        }
        const blob = await fileResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName; // Use the file name provided by the API
        a.click();
        window.URL.revokeObjectURL(url); // Clean up the URL object
      } else {
        throw new Error("Failed to get presigned URL");
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {status?.new && (
        <Box sx={{ p: 2, border: "1px solid #ddd", borderRadius: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="#525455">
            New Request
          </Typography>
          <Typography variant="body" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Created On:</strong> {createdOn}
          </Typography>
        </Box>
      )}

      {status?.closed && (
        <Box
          sx={{
            p: 2,
            border: "1px solid #ddd",
            borderRadius: 2,
            flexDirection: "column",
            display: "flex",
          }}
        >
          <Typography variant="h6" fontWeight={700} color="#525455">
            Closed Request
          </Typography>
          <Typography variant="body" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Closed On:</strong> {closedOn}
          </Typography>
          <Typography variant="body" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Closed By:</strong> {closedBy}
          </Typography>
          <Typography variant="body" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Attachment:</strong>{" "}
            <Box
              sx={{
                display: "inline-flex", // Aligns both the text and icon on the same line
                alignItems: "center", // Ensures that the text and icon are vertically aligned
                gap: "8px", // Space between file name and download icon
              }}
            >
              <Typography
                color="primary"
                fontWeight="bold"
                textTransform="lowercase"
              >
                {attachment}
              </Typography>
              <FileDownload
                fontSize="small"
                sx={{ cursor: "pointer" }} // Makes the icon clickable
                onClick={() => handleDownload(attachment)}
              />
            </Box>
          </Typography>
          <Typography variant="body" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Comment:</strong> {comment !== "" ? comment : "--"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const DataRequestDetails = () => {
  const [consentInfo, setConsentInfo] = useState({});
  const [timezone, setTimezone] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState([]);
  const { id } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [reAssignModalOpen, setReAssignModalOpen] = useState(false);
  const [closedDate, setClosedDate] = useState(null);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [closedBy, setClosedBy] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [closedDateError, setClosedDateError] = useState("");
  const [closedByError, setClosedByError] = useState("");
  const [assignee, setAssignee] = useState("");

  // Function to fetch data from the API
  const fetchData = async () => {
    try {
      const response = await getDataRequest(id);
      setConsentInfo(response.data || {});
      setLoading(false);
    } catch (err) {
      showErrorToast(err);
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const res = await getUserData();
      if (res?.status_code === 200) {
        setUserData(res?.data);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  };

  const getWebsiteDetails = async () => {
    try {
      const websiteId = localStorage.getItem("website_id");
      const websiteData = await getWebsiteById(websiteId);
      const timezoneFromApi = websiteData?.data?.time_zone;
      setTimezone(timezoneFromApi); // Save the timezone from the website details
    } catch (err) {
      throw err;
    }
  };

  const fetchCurrentUser = async () => {
    const data = await fetchUserAttributes();
    setCurrentUser(data);
  };

  useEffect(() => {
    fetchData();
    fetchUserData();
    getWebsiteDetails();
    fetchCurrentUser();
    //eslint-disable-next-line
  }, []);

  const breadcrumbItems = [
    {
      text: "Data Requests",
      href: "/data-requests",
    },
    {
      text: "Data Request Details",
    },
  ];

  const tabs = [
    {
      label: "Data Request Logs",
      value: "1",
      content: (
        <Box>
          <RequestLog
            status={consentInfo?.status} // Dynamically pass the status
            createdOn={
              getFormattedTimestamp(
                consentInfo?.status?.new?.created_on,
                timezone
              ) || ""
            }
            closedOn={
              getFormattedTimestamp(
                consentInfo?.status?.closed?.closed_on,
                timezone
              ) || ""
            }
            closedBy={
              currentUser?.zoneinfo === "Billing_Admin"
                ? userData.find(
                    (user) =>
                      user.user_id === consentInfo?.status?.closed?.close_by
                  )?.full_name ||
                  "Unknown" ||
                  ""
                : consentInfo?.status?.closed?.close_user || ""
            }
            attachment={consentInfo?.status?.closed?.attachment || ""}
            comment={consentInfo?.status?.closed?.comments || ""}
          />
        </Box>
      ),
    },
  ];

  const assigneeName =
    currentUser?.zoneinfo === "Billing_Admin"
      ? userData.find((user) => user.user_id === consentInfo.assignee)
          ?.full_name || "Unknown User"
      : consentInfo.assignee_name;

  // Determine status message based on status object
  const statusMessage = consentInfo?.status?.closed ? "Closed" : "New";

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    backgroundColor: "#F9FAFB",
    borderRadius: "16px",
    boxShadow: 24,
    p: 3,
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setFile(null);
    setClosedDate(null);
    setMessage("");
    setClosedBy("");
    setFileError("");
  };

  const minDate = dayjs(consentInfo?.status?.new?.created_on).startOf("day");
  const maxDate = dayjs().endOf("day"); // Allow full today

  const handleDateChange = (date) => {
    if (
      date &&
      (date.isBefore(minDate, "day") || date.isAfter(maxDate, "day"))
    ) {
      setClosedDateError("Date must be within the allowed range.");
      setClosedDate(null);
    } else {
      setClosedDate(date);
      setClosedDateError(""); // Clear error when valid date is selected
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      const allowedFormats = ["application/pdf", "image/jpeg", "image/jpg"];
      if (/\s/.test(selectedFile.name)) {
        setFileError(
          "File name with spaces is not allowed. Please rename the file."
        );
        return;
      }
      if (!allowedFormats.includes(selectedFile.type)) {
        setFileError(
          "Invalid file type! Please upload a .jpg, .jpeg, or .pdf file."
        );
        return;
      }
      if (selectedFile.size > maxSize) {
        setFileError("File size exceeds 10MB. Please upload a smaller file.");
        return;
      }
      setFile(selectedFile);
      setFileError("");
      setClosedDateError("");
      setClosedByError("");
    }
  };

  const handleSubmit = async () => {
    // Reset previous errors
    setClosedDateError("");
    setFileError("");
    setClosedByError("");

    // Validate fields
    let isValid = true;

    if (!closedDate) {
      setClosedDateError("Closed date is required!");
      isValid = false;
    }

    if (!file) {
      setFileError("Attachment is required!");
      isValid = false;
    }

    if (!closedBy) {
      setClosedByError("Closed by is required!");
      isValid = false;
    }

    if (!isValid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Step 1: Get the presigned URL
      const { presigned_url, file_name } = await generatePresignedUrl(
        file.name
      );

      if (!presigned_url) {
        throw new Error("Failed to get presigned URL");
      }

      // Step 2: Upload file to the presigned URL
      const uploadResponse = await fetch(presigned_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("File upload failed!");
      }

      // Step 3: Call the update status API with the uploaded file's URL
      const payload = {
        status: {
          closed_on: closedDate,
          attachment: file_name, // Use presigned URL
          close_by:
            currentUser.zoneinfo === "Billing_Admin"
              ? closedBy
              : consentInfo?.assignee,
          comments: message,
        },
      };
      const response = await updateStatusChange(
        consentInfo?.request_id,
        payload
      );
      if (response.status_code === 200) {
        const res = await getDataRequest(id);
        setConsentInfo(res.data || {});
        showSuccessToast(response.message);
        handleCloseModal();
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      showErrorToast(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const isSaveDisabled = loading || assignee === consentInfo?.assignee;

  const handleAssigneeSubmit = async () => {
    setLoading(true);
    if (!assignee) {
      return;
    }
    const payload = {
      user_id: assignee,
    };
    try {
      const response = await assignUser(consentInfo?.request_id, payload);
      if (response.status_code === 200) {
        const res = await getDataRequest(id);
        setConsentInfo(res.data || {});
        showSuccessToast(response.message);
        setReAssignModalOpen(false);
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 3, justifyContent: "space-between" }}>
        <Box>
          <CommonHeader
            title="Data Request Details"
            subTitle="You can review the data request details and status update on this page."
          />
        </Box>
        <Box display="flex" gap={2}>
          <ButtonCommon
            onClick={() => {
              setModalOpen(true);
              const matchingUser = userData.find(
                (user) => user.user_id === consentInfo.assignee
              );
              setClosedBy(
                currentUser.zoneinfo === "Billing_Admin"
                  ? matchingUser
                    ? matchingUser.user_id
                    : ""
                  : consentInfo.assignee
              );
            }}
            disabled={
              consentInfo?.status?.closed ||
              Object.keys(consentInfo).length === 0
            }
          >
            Change Status
          </ButtonCommon>
          {currentUser && currentUser?.zoneinfo !== "Org_user" && (
            <ButtonCommon
              onClick={() => {
                const matchingUser = userData.find(
                  (user) => user.user_id === consentInfo.assignee
                );
                setAssignee(matchingUser ? matchingUser.user_id : "");
                setReAssignModalOpen(true);
              }}
              disabled={
                consentInfo?.status?.closed ||
                Object.keys(consentInfo).length === 0
              }
            >
              Reassign
            </ButtonCommon>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <CustomBreadcrumb
          items={breadcrumbItems}
          onBackClick={() => navigate("/data-requests")}
        />
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, backgroundColor: "#F9FAFB", height: "100%" }}>
              <Typography
                variant="h6"
                gutterBottom
                color="#525455"
                fontWeight="bold"
              >
                Data Request Information
              </Typography>
              <Divider />
              {consentInfo && (
                <>
                  {Object.entries(consentInfo)
                    .filter(([key]) => key !== "assignee_name") // Exclude assignee_name
                    .map(([key, value], index, array) => (
                      <Box key={key} sx={{ mb: 1, mt: 2 }}>
                        <Typography
                          variant="body"
                          color="text.secondary"
                          sx={{ textTransform: "capitalize", display: "block" }}
                        >
                          {key
                            .replace(/_/g, " ")
                            .replace(/([A-Z])/g, " $1")
                            .toLowerCase()}
                        </Typography>
                        <Typography
                          variant="body2"
                          flexWrap="wrap"
                          sx={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {key === "status"
                            ? statusMessage
                            : key === "assignee"
                            ? assigneeName
                            : value !== ""
                            ? value
                            : "--"}
                        </Typography>
                        {!(index + 1 === array.length) && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Box>
                    ))}
                </>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <CommonTabs value={"1"} tabs={tabs} />
          </Grid>
        </Grid>
      )}
      <Modal open={modalOpen}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              Close Data Request
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">Request ID</Typography>
            <TextField
              value={consentInfo?.request_id || ""}
              disabled
              fullWidth
              sx={{ mt: 1, mb: 1 }}
              size="small"
            />

            {/* Closed Date */}
            <Typography variant="body1" sx={{ mt: 2 }}>
              Closed Date <span style={{ color: "red" }}>*</span>
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={closedDate}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!closedDateError}
                    helperText={closedDateError}
                  />
                )}
              />
            </LocalizationProvider>
            {closedDateError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {closedDateError}
              </Typography>
            )}

            {/* File Upload */}
            <Typography variant="body1" sx={{ mt: 2 }}>
              Attachment <span style={{ color: "red" }}>*</span>
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFile />}
              fullWidth
              sx={{ mt: 1 }}
            >
              {file ? file.name : "Choose File"}
              <input
                type="file"
                accept=".pdf,.jpeg,.jpg"
                onChange={handleFileChange}
                hidden
              />
            </Button>
            <Typography
              variant="caption"
              sx={{ mt: 1, color: "gray", display: "block" }}
            >
              * Accepted file formats: <strong>.jpg, .jpeg, .pdf</strong>
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "gray", display: "block" }}
            >
              * Maximum file size: <strong>10MB</strong>
            </Typography>
            {fileError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {fileError}
              </Typography>
            )}

            {/* Closed By Dropdown */}
            <Typography variant="body1" sx={{ mt: 2 }}>
              Closed By <span style={{ color: "red" }}>*</span>
            </Typography>
            {currentUser?.zoneinfo === "Billing_Admin" ? (
              <Select
                value={closedBy}
                onChange={(event) => {
                  setClosedBy(event.target.value);
                  setClosedByError(""); // Clear error when a value is selected
                }}
                fullWidth
                error={!!closedByError}
                sx={{ mt: 1 }}
                size="small"
              >
                {(() => {
                  const billingAdmin = (
                    Array.isArray(userData) ? userData : []
                  ).find(
                    (user) =>
                      user.email?.toLowerCase() ===
                      currentUser?.email?.toLowerCase()
                  );

                  const assignee = (
                    Array.isArray(userData) ? userData : []
                  ).find((user) => user.user_id === consentInfo?.assignee);

                  const uniqueUsers = [billingAdmin, assignee].filter(
                    (user, index, self) =>
                      user &&
                      self.findIndex((u) => u?.user_id === user?.user_id) ===
                        index
                  );

                  return (Array.isArray(uniqueUsers) ? uniqueUsers : []).map(
                    (user) => (
                      <MenuItem key={user.user_id} value={user.user_id}>
                        {user.full_name}
                      </MenuItem>
                    )
                  );
                })()}
              </Select>
            ) : (
              <TextField
                value={consentInfo?.assignee_name}
                fullWidth
                sx={{ mt: 1 }}
                disabled
              />
            )}
            {closedByError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {closedByError}
              </Typography>
            )}

            {/* Comment */}
            <Typography variant="body1" sx={{ mt: 2 }}>
              Comment
            </Typography>
            <TextField
              label="Comment"
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              sx={{ mt: 1 }}
            />

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <ButtonCommon onClick={handleSubmit} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : "Save"}
              </ButtonCommon>
              <ButtonCommon onClick={() => setModalOpen(false)}>
                Cancel
              </ButtonCommon>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal open={reAssignModalOpen}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              Choose Assignee
            </Typography>
            <IconButton onClick={() => setReAssignModalOpen(false)}>
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box
            sx={{
              borderRadius: "8px",
              mt: 2,
            }}
          >
            <InputLabel
              name="request_id"
              sx={{ display: "block", marginTop: 1 }}
            >
              Request ID
            </InputLabel>
            <TextField
              value={consentInfo?.request_id || ""}
              disabled
              fullWidth
              sx={{ marginTop: 1, mb: 1 }}
              size="small"
            />

            {/* Closed By Dropdown */}
            <InputLabel
              name="Assignee"
              sx={{ display: "block", marginTop: 2 }}
              required
            >
              Assignee
            </InputLabel>
            <Select
              value={assignee || ""} // Ensuring controlled value
              onChange={(event) => setAssignee(event.target.value)}
              fullWidth
              sx={{ marginTop: 1 }}
              size="small"
            >
              {userData.map((user) => (
                <MenuItem key={user.user_id} value={user.user_id}>
                  {user.full_name}
                </MenuItem>
              ))}
            </Select>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, marginTop: 2 }}>
              <ButtonCommon
                onClick={handleAssigneeSubmit}
                disabled={isSaveDisabled}
              >
                {loading ? <CircularProgress size={20} /> : "Save"}
              </ButtonCommon>
              <ButtonCommon onClick={() => setReAssignModalOpen(false)}>
                Cancel
              </ButtonCommon>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DataRequestDetails;
